import * as THREE from 'three';
import EventEmitter from '../Utils/EventEmitter';
import { Sizes } from './Sizes';
import Time from './Time';

export class Input extends EventEmitter {
    static instance = null;

    static getInstance() {
        if (!Input.instance) {
            Input.instance = new Input();
        }
        return Input.instance;
    }
    
    constructor() {
        if (Input.instance) {
            return Input.instance;
        }

        super();

        this.mouse = new THREE.Vector2();
        this.mouseNormalized = new THREE.Vector2();
        this.mousePrevious = new THREE.Vector2();
        this.speed = new THREE.Vector2();
        this.touch = new THREE.Vector2();
        this.touchActive = false;
        this.inputChanged = false;
        
        this.sizes = Sizes.getInstance();
        this.time = Time.getInstance();
        
        this.lastUpdateTime = this.time.current;

        this.bindAll();
        this.initEvents();

        Input.instance = this;
    }

    initEvents() {
        window.addEventListener('mousemove', this.onMouseMove);
        window.addEventListener('touchstart', this.onTouchStart);
        window.addEventListener('touchmove', this.onTouchMove);
        window.addEventListener('touchend', this.onTouchEnd);
        
        // Only listen for tick to check if we need to process input changes
        this.time.on('tick', this.checkUpdate);
    }

    bindAll() {
        this.onMouseMove = this.onMouseMove.bind(this);
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
        this.checkUpdate = this.checkUpdate.bind(this);
    }

    onMouseMove(event) {
        this.mousePrevious.copy(this.mouse);
        this.mouse.x = event.clientX;
        this.mouse.y = event.clientY;
        
        // Calculate normalized coordinates right when we receive input
        this.mouseNormalized.x = (this.mouse.x / this.sizes.width) * 2 - 1;
        this.mouseNormalized.y = -((this.mouse.y / this.sizes.height) * 2 - 1);
        
        // Mark that input has changed and needs processing
        this.inputChanged = true;
    }   

    onTouchStart(event) {
        this.touchActive = true;
        this.onTouchMove(event);
    }

    onTouchMove(event) {
        if (!this.touchActive) return;
        
        this.touch.x = event.touches[0].clientX;
        this.touch.y = event.touches[0].clientY;
        
        // Update mouse position with touch for unified input handling
        this.mousePrevious.copy(this.mouse);
        this.mouse.x = this.touch.x;
        this.mouse.y = this.touch.y;
        
        // Calculate normalized coordinates
        this.mouseNormalized.x = (this.mouse.x / this.sizes.width) * 2 - 1;
        this.mouseNormalized.y = -((this.mouse.y / this.sizes.height) * 2 - 1);
        
        // Mark that input has changed and needs processing
        this.inputChanged = true;
    }

    onTouchEnd() {
        this.touchActive = false;
    }

    // Check if we need to process input changes on each tick
    checkUpdate() {
        if (this.inputChanged) {
            this.update();
            this.inputChanged = false;
        }
    }

    // Update input state and trigger a single consolidated event
    update() {
        const currentTime = this.time.current;
        const deltaTime = (currentTime - this.lastUpdateTime) * 0.001; // Convert to seconds
        
        if (deltaTime > 0) {
            // Calculate mouse movement speed
            this.speed.x = (this.mouse.x - this.mousePrevious.x) / deltaTime;
            this.speed.y = (this.mouse.y - this.mousePrevious.y) / deltaTime;
            
            // Create a consolidated input state object
            const inputState = {
                mouse: { x: this.mouse.x, y: this.mouse.y },
                normalized: { x: this.mouseNormalized.x, y: this.mouseNormalized.y },
                speed: { x: this.speed.x, y: this.speed.y },
                touch: { x: this.touch.x, y: this.touch.y, active: this.touchActive },
                deltaTime
            };
            
            this.trigger('update', [inputState]);
        }
        
        this.lastUpdateTime = currentTime;
    }
    
    destroy() {
        // Remove event listeners
        window.removeEventListener('mousemove', this.onMouseMove);
        window.removeEventListener('touchstart', this.onTouchStart);
        window.removeEventListener('touchmove', this.onTouchMove);
        window.removeEventListener('touchend', this.onTouchEnd);
        
        // Remove time update binding
        this.time.off('tick', this.checkUpdate);
        
        // Reset instance
        Input.instance = null;
    }
    
    reset() {
        this.mouse.set(0, 0);
        this.mouseNormalized.set(0, 0);
        this.mousePrevious.set(0, 0);
        this.speed.set(0, 0);
        this.touch.set(0, 0);
        this.touchActive = false;
        this.inputChanged = false;
        this.lastUpdateTime = this.time.current;
    }
}